/**
 * Artifacts: typed client over the local `api` helper (plan 5.2).
 *
 * Every response is the `{ data, error, meta }` envelope. Signed URLs are
 * short-lived (15 minutes) and owner scoped: mint one right before it is used
 * and never persist it. A foreign, deleted or unknown id is a 404
 * `ARTIFACT_NOT_FOUND`, deliberately indistinguishable.
 */
import type {
  AgentArtifactDetail,
  AgentArtifactKind,
  AgentArtifactStatus,
  AgentArtifactList,
  APIResponse,
  ArtifactDownloadVariant,
  ArtifactPreviewVariant,
  ArtifactSignedUrl,
} from '@tabbio/types';
import { api } from './api';

export class ArtifactsApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    /** What the server attached (`meta.details`), for example `headVersionId` on a 409. */
    readonly details: Record<string, unknown> = {},
    /** A plan wall's decision (`meta.decision`), for example on an image edit. */
    readonly decision: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ArtifactsApiError';
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** The envelope's error with its details, or null. Shared by the edits client. */
export function artifactsErrorFromEnvelope<T>(res: APIResponse<T>): ArtifactsApiError | null {
  if (!res.error) return null;
  const meta = record(res.meta) ?? {};
  const errorDetails = record((res.error as { details?: unknown }).details) ?? {};
  return new ArtifactsApiError(
    res.error.message,
    res.error.code,
    { ...errorDetails, ...(record(meta.details) ?? {}) },
    record(meta.decision),
  );
}

export function unwrapArtifactsResponse<T>(res: APIResponse<T>): T {
  const error = artifactsErrorFromEnvelope(res);
  if (error) throw error;
  if (res.data == null) {
    throw new ArtifactsApiError('Empty response', 'EMPTY_RESPONSE');
  }
  return res.data;
}

const unwrap = unwrapArtifactsResponse;

const path = (id: string) => `/api/artifacts/${encodeURIComponent(id)}`;

/** GET /api/artifacts/:id */
export async function getArtifact(id: string): Promise<AgentArtifactDetail> {
  return unwrap(await api.get<AgentArtifactDetail>(path(id)));
}

/** GET /api/artifacts?threadId=&runId=&kind=&status=&limit=&cursor= */
export async function listArtifacts(
  params: {
    threadId?: string;
    runId?: string;
    kind?: AgentArtifactKind;
    status?: AgentArtifactStatus;
    limit?: number;
    cursor?: string;
  } = {},
): Promise<AgentArtifactList> {
  const query = new URLSearchParams();
  if (params.threadId) query.set('threadId', params.threadId);
  if (params.runId) query.set('runId', params.runId);
  if (params.kind) query.set('kind', params.kind);
  if (params.status) query.set('status', params.status);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.cursor) query.set('cursor', params.cursor);
  const suffix = query.toString();
  return unwrap(
    await api.get<AgentArtifactList>(`/api/artifacts${suffix ? `?${suffix}` : ''}`),
  );
}

/**
 * GET /api/artifacts/:id/preview-url?variant=&theme=&versionId= (inline
 * disposition). `theme=dark` serves the HTML page rendered in the dark theme;
 * the API ignores it for other variants and defaults to light. Without
 * `versionId` the link is for the head.
 */
export async function mintPreviewUrl(
  id: string,
  variant: ArtifactPreviewVariant,
  theme: 'light' | 'dark' = 'light',
  versionId?: string | null,
): Promise<ArtifactSignedUrl> {
  let query = theme === 'dark' ? `variant=${variant}&theme=dark` : `variant=${variant}`;
  if (versionId) query += `&versionId=${encodeURIComponent(versionId)}`;
  return unwrap(await api.get<ArtifactSignedUrl>(`${path(id)}/preview-url?${query}`));
}

/**
 * GET /api/artifacts/:id/download-url?variant=&versionId= (attachment
 * disposition). A PDF or deck a manual save left stale answers 409
 * `ARTIFACT_NOT_READY` (`details.preparing`) and starts rendering it.
 */
export async function mintDownloadUrl(
  id: string,
  variant: ArtifactDownloadVariant,
  versionId?: string | null,
): Promise<ArtifactSignedUrl> {
  const version = versionId ? `&versionId=${encodeURIComponent(versionId)}` : '';
  return unwrap(
    await api.get<ArtifactSignedUrl>(`${path(id)}/download-url?variant=${variant}${version}`),
  );
}

/** DELETE /api/artifacts/:id (soft delete, owner only). */
export async function deleteArtifact(
  id: string,
): Promise<{ id: string; deleted: boolean }> {
  return unwrap(await api.delete<{ id: string; deleted: boolean }>(path(id)));
}
